import { APP_CONFIG } from '@sd/shared';
import { sortBy } from 'ramda';
import { DefaultValue, selector, selectorFamily } from 'recoil';
import { sdGatewayClient } from '../../http/sdGatewayClient';
import { AccountGroup } from '../../models/AccountGroup';
import { PreferenceInstance } from '../../models/RulesAndPreferencesGraphql';
import { SDUserAccount } from '../../models/SDUser';
import { accountGroupService } from '../../services/accountGroupService';
import { gql } from '../../utils/gql';
import { currentUserState } from '../auth/authState';
import { userPreferencesState } from '../userPreferences/userPreferencesState';

/**
 * Gets all associated accounts for the current user (including those from vicki override account groups)
 */
export const userAccountsState = selector<SDUserAccount[]>({
  key: 'sd:accounts',
  get: async ({ get }) =>
    sortBy(
      item => item.name.toLowerCase(),
      get(availableAccountGroupsState).flatMap(ag => ag.accounts)
    )
});

/**
 * Gets all avilable accounts defined on a user (does not include vicki overrides)
 */
export const selectedAccountState = selector<SDUserAccount | undefined>({
  key: 'sd:selectedAccount',
  get: ({ get }) => {
    const userAccounts = get(userAccountsState);
    const userPreferences = get(userPreferencesState);
    return userAccounts.find(a => a.name === userPreferences?.accountName);
  }
});

/**
 * Gets/sets the globally selected seller account number for the user
 */
export const selectedSellerNumberState = selector<number | undefined>({
  key: 'sd:selectedSellerNumber',
  get: ({ get }) => {
    const accountNumber = get(userPreferencesState)?.accountNumber;
    const availableAccountGroups = get(availableAccountGroupsState);
    const validSellerNumber =
      accountNumber && availableAccountGroups.some(a => a.sellerNumbers.includes(Number(accountNumber)));
    return (validSellerNumber && Number(accountNumber)) || undefined;
  },
  set: (o, newValue) => {
    const currentUser = o.get(currentUserState);
    const availableAccountGroups = o.get(availableAccountGroupsState);
    if (newValue instanceof DefaultValue) return;
    const newAccount = newValue ? currentUser?.accounts.find(a => a.sellerNumbers.includes(newValue)) : undefined;
    const validSellerNumber = newValue && availableAccountGroups.some(a => a.sellerNumbers.includes(newValue));
    o.set(
      userPreferencesState,
      prefs =>
        prefs && {
          ...prefs,
          accountName: newAccount?.name || '',
          accountNumber: validSellerNumber ? String(newValue) : ''
        }
    );
  }
});

/**
 * Gets/sets the globally selected account group for the user
 */
export const selectedAccountGroupState = selector<AccountGroupWithAccounts | undefined>({
  key: 'sd:selectedAccountGroup',
  get: ({ get }) => {
    const selectedAccountGroupId = get(userPreferencesState)?.accountGroup;
    const availableAccountGroups = get(availableAccountGroupsState);
    return typeof selectedAccountGroupId === 'number'
      ? availableAccountGroups.find(g => g.id === selectedAccountGroupId)
      : undefined;
  },
  set: ({ set, get }, newValue) => {
    if (newValue instanceof DefaultValue) return;
    const userPreferences = get(userPreferencesState);
    set(
      userPreferencesState,
      userPreferences && { ...userPreferences, accountGroup: newValue?.id, accountName: '', accountNumber: '' }
    );
  }
});

/**
 * Gets the relevant seller numbers of the current account group ignoring selection
 *
 * In most cases `selectedAccountGroupSellerNumbersState` should be used instead
 */
export const relatedAccountGroupSellerNumbersState = selector<number[] | undefined>({
  key: 'sd:relatedAccountGroupSellerNumbersState',
  get: ({ get }) => {
    const selectedAccountGroup = get(selectedAccountGroupState);
    if (!selectedAccountGroup) return [];
    return selectedAccountGroup.sellerNumbers;
  }
});

/**
 * Gets/sets the globally selected seller numbers for the account group
 */
export const selectedAccountGroupSellerNumbersState = selector<number[]>({
  key: 'sd:selectedAccountGroupSellerNumbers',
  get: ({ get }) => {
    const selectedAccountGroup = get(selectedAccountGroupState);
    const userPreferences = get(userPreferencesState);
    if (!userPreferences || !selectedAccountGroup) return [];
    return selectedAccountGroup.accounts
      .filter(a => !userPreferences.deselectedAccounts?.includes(a.guid))
      .flatMap(a => a.sellerNumbers);
  }
});

export type AccountGroupWithAccounts = AccountGroup & {
  accounts: SDUserAccount[];
};

export const availableAccountGroupsState = selector<AccountGroupWithAccounts[]>({
  key: 'sd:availableAccountGroups',
  get: async ({ get }) => {
    const currentUser = get(currentUserState);
    if (!currentUser) return [];
    const [accountGroupsForSellerNumber, vickiOverrideAccountGroups] = await Promise.all([
      accountGroupService.search({ sellerNumbers: currentUser.sellerNumbers }),
      accountGroupService.search({ username: currentUser.username })
    ]);

    const groups: AccountGroupWithAccounts[] = sortBy(
      item => item.name.toLowerCase(),
      [
        // Vicki override groups get applied with ALL accounts, even if the seller numbers
        // are not actually assigned to the user in Vicki
        ...vickiOverrideAccountGroups.map(ag => {
          const vickiAccounts = currentUser.accounts.filter(ac =>
            ac.sellerNumbers.some(sn => ag.sellerNumbers.includes(sn))
          );
          return {
            ...ag,
            accounts: sortBy(
              item => item.name.toLowerCase(),
              [
                ...vickiAccounts,
                {
                  guid: 'vicki-overrides',
                  name: 'other',
                  // All the leftover accounts go in the "Other" group
                  sellerNumbers: ag.sellerNumbers.filter(sn => !vickiAccounts.some(a => a.sellerNumbers.includes(sn)))
                }
              ]
            )
          };
        }),
        ...accountGroupsForSellerNumber
          // Don't duplicate vicki override groups
          .filter(ag => !vickiOverrideAccountGroups.some(voag => voag.id === ag.id))
          .map(ag => ({
            ...ag,
            accounts: sortBy(
              item => item.name.toLowerCase(),
              currentUser.accounts.filter(ac => ac.sellerNumbers.some(sn => ag.sellerNumbers.includes(sn)))
            )
          }))
      ].filter(ag => ag.accounts.length > 0)
    );
    const accountedForSellerNumbers = groups.flatMap(g => g.accounts.flatMap(a => a.sellerNumbers));
    const remainingAccounts = sortBy(
      item => item.name.toLowerCase(),
      currentUser.accounts.reduce<SDUserAccount[]>((acc, account) => {
        // Not sure why but Legacy SD displays accounts that have 0 seller numbers in them
        const showMe = account.sellerNumbers.every(
          sn =>
            // Is it already displayed in another account group
            !accountedForSellerNumbers.includes(sn) &&
            // Are all the seller numbers accounted for in another account in
            // this fake group
            !acc.some(a => a.sellerNumbers.includes(sn))
        );
        return showMe ? [...acc, account] : acc;
      }, [])
    );

    return [
      // Inject a "fake" account group that contains all the leftover accounts a user has
      // access to but do not fall into an account group.
      ...(remainingAccounts.length
        ? [
            {
              id: 0,
              name: 'Account(s)',
              accounts: remainingAccounts,
              // This hardcoded value is temporary and will change with US866832
              accountGroupConfigId: ['uat', 'prod'].includes(APP_CONFIG.build.environment) ? 1 : 5,
              sellerNumbers: remainingAccounts.flatMap(a => a.sellerNumbers)
            }
          ]
        : []),
      ...groups
    ];
  }
});

const BULK_NAME_AND_VALUE_QUERY = gql`
  query ($input: [PreferenceQueryInput!]!) {
    bulkPreferences(input: $input) {
      name
      value
    }
  }
`;

/**
 * The full list of preferences based on the current user and rollup
 *
 * Note: Rollup preferences should only be used when the preference applies
 * to the entire page and not specific vehicles
 */
export const preferencesForRollupState = selector<PreferenceInstance[]>({
  key: 'sd:rollupPreferences',
  get: async ({ get }) => {
    const selectedAccountRollup = get(selectedAccountGroupState);
    const user = get(currentUserState);
    const response = await sdGatewayClient.post('/preferences/graphql', {
      query: BULK_NAME_AND_VALUE_QUERY,
      variables: {
        input: [
          {
            username: user?.username,
            accountRollup: `${selectedAccountRollup?.id}`
          }
        ]
      }
    });
    return response.data.data.bulkPreferences[0];
  }
});

/**
 * Get a rollup preference value
 *
 * Note: Rollup preferences should only be used when the preference applies
 * to the entire page and not specific vehicles
 */
export const selectRollupPreference = selectorFamily({
  key: 'sd:selectRollupPreference',
  get: (name: string) => {
    return ({ get }) => {
      const prefs = get(preferencesForRollupState);
      return prefs.filter(p => p.name === name)[0]?.value;
    };
  }
});
